/* eslint-disable react/forbid-prop-types */
import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import useOnBoarding from 'hooks/useOnBoarding';
import { ONBOARDING_MAX_STEP } from 'features/onboarding/constants';
import { selectors as credentialsSelectors } from 'lib/entities/credentials';

// Local Components
import useProfileData from 'hooks/useProfileData';
import useNotificationListener from 'hooks/useNotificationListener';
import { effects as notificationsEffects } from 'features/edit-profile/slice';
import useChatListenerWsSocialV2 from 'hooks/useChatListenerWsSocialV2';
import { useGetReferralInfo } from 'features/community/hooks/useGetReferralInfo';
import { useGetChatUnreadCount } from 'stores/chat/useGetChatUnreadCount';
import TradingContextProvider from 'features/transaction/provider';
import Flex from '@deprecated-ui/common/Flex';
// import webSocketSlice from 'global/WebSocketV2/slice';
import { useRouter } from 'next/router';
import Amend from 'global/TableOrderbookWidget/components/modals/Amend';
import Container, { Wrapper, WrapperMainContent } from './styled';

// local redux slice
import { selectors } from './slice';
import { DATA_CY_MAIN_LAYOUT } from './__tests__/constant';

const HeaderWeb = dynamic(() => import('../Header/Web'), { ssr: false });
const HeaderMobile = dynamic(() => import('../Header/Mobile'), { ssr: false });
const SideWidget = dynamic(() => import('./SideWidget'), { ssr: false });
const OnBoarding = dynamic(() => import('features/onboarding'), { ssr: false });
const ModalTransactionSecurities = dynamic(
  () => import('global/TradingModal'),
  { ssr: false },
);
const ModalAuthSecurities = dynamic(
  () => import('features/securities/AuthModal'),
  {
    ssr: false,
  },
);
const ModalShareTradeManager = dynamic(
  () => import('features/sharetrade/components/ModalShareTradeManager'),
  { ssr: false },
);
const GlobalModalManager = dynamic(() => import('global/GlobalModalManager'), {
  ssr: false,
});
const ModalBonds = dynamic(
  () => import('global/TradingModal/RealTradingModal/ModalBonds'),
  { ssr: false },
);
const ModalUnavailable = dynamic(
  () => import('features/company/FRPage/ModalUnavailable'),
  { ssr: false },
);
const ModalErrorManager = dynamic(
  () => import('./ModalErrorMessage/ModalErrorManager'),
  { ssr: false },
);
const MultiPortoModalManager = dynamic(
  () => import('features/multi-porto/components/ModalManager'),
  { ssr: false },
);
const StreamErrorModalManager = dynamic(
  () => import('features/stream/ModalError/ModalManager'),
  { ssr: false },
);
const DatafeedModalManager = dynamic(
  () => import('global/Datafeed/modal/ModalManager'),
  { ssr: false },
);
const InsufficientTradingBalanceModal = dynamic(
  () =>
    import(
      'global/TableOrderbookWidget/components/table/tablePrice/InsufficientTradingBalanceModal'
    ),
  { ssr: false },
);

function MainLayout({
  children,
  isFlexibleWidth,
  maxWidth,
  background,
  width,
  fullPage = false,
}) {
  // Library hooks
  const router = useRouter();
  const dispatch = useDispatch();
  const breakpoints = useBreakpoint();

  // Zustand store
  const { fetchChatUnreadCount, isChatUnreadCountReset } =
    useGetChatUnreadCount((s) => ({
      fetchChatUnreadCount: s.fetchUnreadCount,
      isChatUnreadCountReset: s.isReset,
    }));
  const fetchCommunityInfo = useGetReferralInfo((s) => s.fetchCommunityInfo);

  // Redux store
  const { isLoggedIn } = useSelector(credentialsSelectors);
  const { isSideWidgetOpen, isHeaderMinimized, isMarketOpen } =
    useSelector(selectors);

  // Custom hooks
  const { onboardingProgress } = useOnBoarding();
  useProfileData();
  useNotificationListener(isLoggedIn);
  useChatListenerWsSocialV2(isLoggedIn);

  // Reactive variable
  const isShowOnboarding = onboardingProgress < ONBOARDING_MAX_STEP;
  const props = width ? { style: { width } } : {};

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    // During route changes or reload:
    // re-fetch unread chat if state is haven't reset
    // or clear chat data if state have reset
    // if (!isChatUnreadCountReset) {
    //   fetchChatUnreadCount();
    // } else {
    //   dispatch(webSocketSlice.actions.clearChat());
    // }

    fetchChatUnreadCount();
    fetchCommunityInfo();
    dispatch(notificationsEffects.getNotifications());
  }, [isLoggedIn]);

  useEffect(() => {
    const { hash } = window.location;
    const { pathname } = router;
    if (pathname !== '/' && hash.startsWith('#/')) {
      const strippedUrl = hash.replace('#', '');
      if (
        router.pathname.includes(strippedUrl) ||
        strippedUrl.includes(router.pathname)
      ) {
        router.replace(router.pathname);
      } else {
        router.push(strippedUrl);
      }
    }
  }, []);

  // React render
  if (isShowOnboarding) {
    return <OnBoarding progress={onboardingProgress} />;
  }
  return (
    <div data-cy={DATA_CY_MAIN_LAYOUT}>
      <TradingContextProvider>
        <Flex justify="space-between">
          <WrapperMainContent
            isSideWidgetOpen={isSideWidgetOpen}
            isMarketOpen={isMarketOpen}
          >
            {breakpoints.md ? (
              <HeaderWeb
                isSideWidgetOpen={isSideWidgetOpen}
                isMarketOpen={isMarketOpen}
                isHeaderMinimized={isHeaderMinimized}
              />
            ) : (
              <HeaderMobile />
            )}
            <Wrapper
              background={background}
              isSideWidgetOpen={isSideWidgetOpen}
              {...props}
            >
              <Container
                id="main-container"
                isFlexibleWidth={isFlexibleWidth}
                maxWidth={maxWidth}
                isSideWidgetOpen={isSideWidgetOpen}
                fullPage={fullPage}
                isMarketOpen={isMarketOpen}
              >
                {children}
              </Container>
            </Wrapper>
          </WrapperMainContent>
          <SideWidget />
        </Flex>

        <ModalErrorManager />
        <ModalAuthSecurities />
        <ModalTransactionSecurities />
        <GlobalModalManager />
        <ModalBonds />
        <ModalShareTradeManager />

        {/* Modal Unavailable FR for Transaction */}
        <ModalUnavailable />

        {/* Multi Porto Modal */}
        <MultiPortoModalManager />

        {/* Stream Error Modal */}
        <StreamErrorModalManager />

        {/* Insufficient Trading Balance Modal */}
        <InsufficientTradingBalanceModal />

        {/* Datafeed Modal */}
        <DatafeedModalManager />
        <Amend />
      </TradingContextProvider>
    </div>
  );
}

MainLayout.propTypes = {
  children: PropTypes.node.isRequired,
  isFlexibleWidth: PropTypes.bool,
  maxWidth: PropTypes.number,
  background: PropTypes.string,
  width: PropTypes.any,
};

MainLayout.defaultProps = {
  isFlexibleWidth: false,
  maxWidth: undefined,
  background: 'transparent',
  width: undefined,
};

export default MainLayout;
